/* ════════════════════════════════════════════════════════════════════════
   Member Intelligence — the Family Profile data model (v2).
   One source of truth for who each resident is. Consumed by the Family
   Room today; shaped so Kitchen / Planning / Chores / Goals / Wellness
   can consume it later without a rebuild.
   ════════════════════════════════════════════════════════════════════════ */

export const RELATIONSHIPS = [
  "Parent", "Partner / Spouse", "Child", "Sibling", "Grandparent",
  "Grandchild", "Aunt / Uncle", "Cousin", "Other family", "Friend",
  "Flatmate / Housemate", "Carer", "Other",
];

export const LIFE_STAGES = ["Baby", "Child", "Teen", "Adult", "Elder"];

export const GOAL_CATEGORIES = [
  "Personal", "Health & wellbeing", "Learning", "School", "Career",
  "Money", "Travel", "Family", "Home", "Creative", "Adventure", "Other",
];

export const FAVOURITE_CATEGORIES = [
  "Colour", "Hobby", "Activity", "Sport", "Music", "Movies & shows",
  "Books", "Places", "Animals", "Flowers", "Treats", "Loves", "Dislikes",
];

export const RESPONSIBILITY_SUGGESTIONS = [
  "School administration", "Cooking", "Bins", "Pet care", "Bills",
  "Groceries", "Laundry", "School pickup", "Garden", "Appointments", "Maintenance",
];

export const FOOD_FIELDS = [
  ["favouriteFoods", "Favourite foods"],
  ["favouriteMeals", "Favourite meals"],
  ["likes", "Foods they like"],
  ["dislikes", "Foods they dislike"],
  ["wontEat", "Will not eat"],
  ["dietary", "Dietary preferences"],
  ["allergies", "Food allergies"],
  ["intolerances", "Intolerances"],
  ["textures", "Texture & sensory preferences"],
  ["snacks", "Favourite snacks"],
  ["lunchbox", "Lunchbox favourites"],
  ["takeaway", "Takeaway & eating out"],
];

export const CARE_FIELDS = [
  ["allergies", "Allergies (non-food)"],
  ["accessibility", "Accessibility needs"],
  ["sensory", "Sensory needs"],
  ["support", "Support needs"],
  ["mobility", "Mobility considerations"],
  ["communication", "Communication preferences"],
  ["context", "Important care context"],
];

export const LIVING_TEXT_FIELDS = [
  ["wakeTime", "Preferred wake time", "e.g. 6:30am"],
  ["bedtime", "Preferred bedtime", "e.g. 9:30pm"],
  ["appointmentTimes", "Preferred appointment times", "e.g. Mornings, never Mondays"],
  ["rhythm", "School / work rhythm", "e.g. School Mon–Fri, netball Saturdays"],
  ["quietTime", "Quiet-time preference", "e.g. Needs an hour alone after work"],
  ["studyPreference", "Study / homework preference", "e.g. Straight after school, at the kitchen table"],
  ["chorePreferences", "Chore preferences", "e.g. Prefers outdoor jobs, hates dishes"],
  ["planningPreferences", "Planning preferences", "e.g. Likes the week mapped on Sundays"],
  ["notificationPreferences", "Notification preferences", "e.g. Text only, nothing before 8am"],
];

export const CELEBRATION_LIST_FIELDS = [
  ["giftIdeas", "Gift ideas"],
  ["wants", "Things they currently want"],
  ["stores", "Favourite stores"],
  ["treats", "Favourite treats"],
  ["experiences", "Favourite experiences"],
  ["placesToVisit", "Places they'd like to visit"],
];

export function blankIntelligence() {
  return {
    preferredName: "",
    pronouns: "",
    contactInfo: "",
    relationships: [],
    lifeStage: "",
    birthday: "",
    food: {
      favouriteFoods: [], favouriteMeals: [], likes: [], dislikes: [],
      wontEat: [], dietary: [], allergies: [], intolerances: [],
      textures: [], snacks: [], lunchbox: [], takeaway: [],
    },
    likesFavourites: [],
    care: {
      allergies: [], accessibility: [], sensory: [],
      support: [], mobility: [], communication: [], context: [],
    },
    living: {
      chronotype: "", wakeTime: "", bedtime: "", appointmentTimes: "",
      rhythm: "", quietTime: "", studyPreference: "", chorePreferences: "",
      planningPreferences: "", notificationPreferences: "", commitments: [],
    },
    responsibilities: [],
    celebrations: {
      giftIdeas: [], wants: [], stores: [], treats: [],
      experiences: [], placesToVisit: [], clothingSize: "", shoeSize: "",
    },
    araShouldKnow: [],
  };
}

const RELATIONSHIP_HINTS = [
  [/^(parent|mum|mom|dad|mother|father|step ?mum|step ?dad)/i, "Parent"],
  [/(partner|spouse|wife|husband)/i, "Partner / Spouse"],
  [/^(child|son|daughter|kid)/i, "Child"],
  [/(sibling|brother|sister)/i, "Sibling"],
  [/(grandm|grandp|grandparent|nana|granddad)/i, "Grandparent"],
  [/(aunt|uncle)/i, "Aunt / Uncle"],
  [/cousin/i, "Cousin"],
  [/friend/i, "Friend"],
  [/(flatmate|housemate|roommate)/i, "Flatmate / Housemate"],
  [/carer/i, "Carer"],
];

/* Idempotent legacy → v2 migration. Never destroys legacy fields. */
export function migrateMemberToV2(member) {
  if (!member || member.intelligenceVersion >= 2) return member;
  const base = blankIntelligence();
  const pushUnique = (arr, v) => {
    const t = String(v || "").trim();
    if (t && !arr.includes(t)) arr.push(t);
  };

  const relationships = [...(member.relationships || [])];
  if (relationships.length === 0 && member.role) {
    for (const [re, rel] of RELATIONSHIP_HINTS) {
      if (re.test(member.role)) { relationships.push(rel); break; }
    }
  }

  const lifeStage = member.lifeStage
    || (LIFE_STAGES.includes(member.ageGroup) ? member.ageGroup : (member.ageGroup || ""));

  (member.preferences || []).forEach(p =>
    base.likesFavourites.push({ id: `fav-${Math.random().toString(36).slice(2, 8)}`, category: "Loves", value: p })
  );
  (member.supportNeeds || []).forEach(s => pushUnique(base.care.support, s));
  (member.profile?.healthNotes || []).forEach(n => pushUnique(base.care.context, n));
  (member.profile?.foodPreferences || []).forEach(f => pushUnique(base.food.likes, f));

  const responsibilities = [];
  (member.responsibilities || []).forEach(r => pushUnique(responsibilities, r));
  (member.profile?.responsibilities || []).forEach(r => pushUnique(responsibilities, r));

  pushUnique(base.araShouldKnow, member.notes);
  if (member.note !== member.notes) pushUnique(base.araShouldKnow, member.note);
  (member.profile?.schoolWorkNotes || []).forEach(n => pushUnique(base.araShouldKnow, n));

  return {
    ...member,
    ...base,
    food: { ...base.food, ...(member.food || {}) },
    care: {
      ...base.care,
      ...(member.care || {}),
      support: [...new Set([...base.care.support, ...(member.care?.support || [])])],
      context: [...new Set([...base.care.context, ...(member.care?.context || [])])],
    },
    living: { ...base.living, ...(member.living || {}) },
    celebrations: { ...base.celebrations, ...(member.celebrations || {}) },
    likesFavourites: (member.likesFavourites?.length ? member.likesFavourites : base.likesFavourites),
    araShouldKnow: [...new Set([...(member.araShouldKnow || []), ...base.araShouldKnow])],
    relationships,
    lifeStage,
    responsibilities,
    intelligenceVersion: 2,
  };
}

export function migrateMembersToV2(members) {
  return (members || []).map(migrateMemberToV2);
}

/* Identity line — "Parent · Adult". */
export function identityLine(member) {
  const parts = [];
  if (member.relationships?.length) parts.push(member.relationships.join(", "));
  else if (member.role) parts.push(member.role);
  if (member.lifeStage) parts.push(member.lifeStage);
  return parts.join(" · ");
}

/* How many things ARA knows about this person. */
export function countKnownThings(member, goals = []) {
  if (!member) return 0;
  let n = 0;
  const scalar = (v) => { if (String(v || "").trim()) n += 1; };
  const list = (arr) => { n += (arr || []).length; };

  scalar(member.preferredName); scalar(member.pronouns); scalar(member.contactInfo);
  scalar(member.birthday);
  list(member.relationships);
  scalar(member.lifeStage);

  const f = member.food || {};
  FOOD_FIELDS.forEach(([k]) => list(f[k]));
  list(member.likesFavourites);
  const c = member.care || {};
  CARE_FIELDS.forEach(([k]) => list(c[k]));
  const l = member.living || {};
  scalar(l.chronotype);
  LIVING_TEXT_FIELDS.forEach(([k]) => scalar(l[k]));
  list(l.commitments);
  list(member.responsibilities);
  const cel = member.celebrations || {};
  CELEBRATION_LIST_FIELDS.forEach(([k]) => list(cel[k]));
  scalar(cel.clothingSize); scalar(cel.shoeSize);
  list(member.araShouldKnow);
  n += goals.length;
  return n;
}
